import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  UseQueryResult,
} from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

// 기존 QueryResponse 인터페이스 유지
interface QueryResponse<T> {
  data: T;
  status: number;
  message?: string;
}

// API 응답 타입을 유연하게 처리하기 위한 타입 가드
function isQueryResponse<T>(
  response: T | QueryResponse<T>
): response is QueryResponse<T> {
  return (
    typeof response === 'object' &&
    response !== null &&
    'data' in response &&
    'status' in response
  );
}

// 수정된 WckQueryOptions 타입
type WckQueryOptions<T> = Omit<
  UseQueryOptions<T | QueryResponse<T>, Error, T, QueryKey>,
  'queryKey' | 'queryFn'
> & {
  queryKey: QueryKey;
  queryFn: () => Promise<T | QueryResponse<T>>;
  initialDataEnabled?: boolean;
};

export const WCK_QUERY_KEY_PREFIX = 'wckQueryKey';

export function useWckQuery<T>(
  options: WckQueryOptions<T>
): UseQueryResult<T, Error> {
  const [isMounted, setIsMounted] = useState(false);
  const {
    queryKey,
    queryFn,
    initialDataEnabled = true,
    ...restOptions
  } = options;

  const infiniteQueryKey = useMemo(
    () => [WCK_QUERY_KEY_PREFIX, ...queryKey],
    [queryKey]
  );

  useEffect(() => {
    if (isMounted) {
      const stateData = window?.history?.state?.filteredQueryData as
        | Record<string, unknown>
        | undefined;
      // 지울 항목이 실제로 있을 때만 replaceState 호출.
      // Safari는 history.replaceState를 10초당 100회로 제한하고 초과 시 예외를
      // 던지는데, 쿼리가 많은 페이지(프로모션 등)에서 마운트 러시에 무조건
      // 호출하면 임계를 넘어 페이지 전체가 죽는다.
      if (
        stateData &&
        Object.prototype.hasOwnProperty.call(stateData, infiniteQueryKey.join())
      ) {
        const filteredQueryData = Object.entries(stateData)
          .filter(item => item[0] !== infiniteQueryKey.join())
          .reduce(
            (acc, [key, value]) => ({
              ...acc,
              [key]: value,
            }),
            {}
          );
        try {
          window.history.replaceState(
            {
              ...window.history.state,
              filteredQueryData,
            },
            '',
            undefined
          );
        } catch (e) {
          // Safari replaceState 호출 제한 — 복원 데이터 정리는 다음 렌더 사이클로 미룬다
        }
      }
    }
    setIsMounted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  const queryData = useMemo(() => {
    if (
      initialDataEnabled &&
      typeof window !== 'undefined' &&
      window?.history?.state?.filteredQueryData
    ) {
      const filteredQueryData =
        window?.history?.state?.filteredQueryData?.[infiniteQueryKey?.join()];
      if (filteredQueryData !== undefined) {
        return filteredQueryData as T;
      }
    }
    return restOptions.initialData;
  }, [infiniteQueryKey, initialDataEnabled, restOptions.initialData]);

  const query = useQuery<T | QueryResponse<T>, Error, T, QueryKey>({
    queryKey: infiniteQueryKey,
    queryFn,
    ...restOptions,
    initialData: queryData,
  });

  return query;
}
